import React, { useEffect, useState } from "react";
import { ModalProps } from "../../../interfaces/modal";
import {
  createAbsence,
  createValeur,
  getAbsence,
  getTypesInputAbscence,
  getTypesInputValeur,
  getValeur,
  updateAbsence,
  updateValeur,
} from "../../../api/social";
import { useSocialStore } from "../../../stores/social";
import { SelectText } from "../../ui/inputs/SelectText";
import { Input } from "../../ui/inputs/Input";
import { TextArea } from "../../ui/inputs/TextArea";
import { showToastError, showToastSuccess } from "../../../services/toast";
import { Checkbox } from "../../ui/inputs/Checkbox";
import {
  formatDateForInput,
  handleCheckIfDateExist,
} from "../../../services/function";
import { getOneCustomerFile } from "../../../api/customerFile";
import Overlap from "./Overlap";
import { useTranslation } from "react-i18next";
import {
  ModalButton,
  ModalSection,
  ModalShell,
} from "../../ui/modals/ModalShell";

const ElementsDetails: React.FC<ModalProps> = ({
  closeModal,
  createOrEdit,
  id,
}) => {
  const { t, i18n } = useTranslation();
  const {
    typesInputValeur,
    setTypesInputValeur,
    tabElements,
    formDataDetails,
    setFormDataDetails,
    idEmployee,
    updateSocial,
    setUpdateSocial,
    idCustomer,
    formData,
    isOverlap,
    setIsOverlap,
  } = useSocialStore();
  const [prolongations, setProlongations] = useState(false);
  const [dateRestrictions, setDateRestrictions] = useState({
    min: "",
    max: "",
  });
  const today = new Date().toISOString().split("T")[0];
  const [datesExist, setDatesExist] = useState([]);
  
  // Permet de récuperer les dates existantes pour check si la date contient deja une absence
  useEffect(() => {
    if (formData?.sc_Absences) {
      const datesExist = formData?.sc_Absences.map((absence: any) => ({
        from: formatDateForInput(absence.from),
        to: formatDateForInput(absence.to),
      }));
      setDatesExist(datesExist);
    }
  }, [formData]);

  // Récupère les restrictions de date basées sur le mois du client
  useEffect(() => {
    if (idCustomer) {
      getOneCustomerFile(idCustomer)
        .then((res) => {
          const month = res.data?.sc_Month;
          if (month) {
            const currentYear = new Date().getFullYear();
            // On calcule le mois précédent
            const previousMonth = month === 1 ? 12 : month - 1;
            const previousYear = month === 1 ? currentYear - 1 : currentYear;

            // Date de début : 1er jour du mois précédent
            const minDate = new Date(previousYear, previousMonth - 1, 1);
            // Date de fin : dernier jour du mois en cours (sc_Month)
            // Exemple: si sc_Month = 6 (juin), on veut le 30/06
            // sc_Month est 1-indexed (1=janvier, 6=juin, 12=décembre)
            // JavaScript est 0-indexed (0=janvier, 5=juin, 11=décembre)
            // new Date(year, month, 0) donne le dernier jour du mois précédent
            // Comme sc_Month est 1-indexed, on utilise month directement (qui est déjà le bon mois en JS)
            const maxDate = new Date(currentYear, month, 0);

            // Formatage en YYYY-MM-DD sans timezone pour éviter les décalages
            const formatDate = (date: Date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            };

            setDateRestrictions({
              min: formatDate(minDate),
              max: formatDate(maxDate),
            });
          }
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  }, [idCustomer]);

  // Recupere les types d'éléments pour chaque type
  useEffect(() => {
    if (tabElements === "Valeurs") {
      getTypesInputValeur(i18n.language.toUpperCase())
        .then((res) => {
          const formattedData = res.data?.map((data: any) => ({
            label: data.name,
            value: data.id,
          }));
          setTypesInputValeur(formattedData.sort((a: any, b: any) => a.label.localeCompare(b.label)));
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    } else {
      getTypesInputAbscence(i18n.language.toUpperCase())
        .then((res) => {
          const formattedData = res.data?.map((data: any) => ({
            label: data.name,
            value: data.id,
          }));
          setTypesInputValeur(formattedData.sort((a: any, b: any) => a.label.localeCompare(b.label)));

          if (createOrEdit === "create" && tabElements === "Absences") {
            setFormDataDetails({...formDataDetails, idTypeInput: 65})
          }
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  }, []);

  // Récupere les données de l'élément à modifier
  useEffect(() => {
    if (createOrEdit === "edit" && tabElements === "Absences") {
      getAbsence(Number(id))
        .then((res) => {
          if (res.data.previousFrom !== "") {
            setProlongations(true);
          }

          setFormDataDetails({
            ...res.data,
            from: formatDateForInput(res.data.from),
            to: formatDateForInput(res.data.to),
            previousFrom: formatDateForInput(res.data.previousFrom),
          });
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    } else if (createOrEdit === "edit" && tabElements === "Valeurs") {
      getValeur(Number(id))
        .then((res) => {
          setFormDataDetails(res.data);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  }, []);

  // Permet de changer le type d'élément
  const handleSelectTextChange = (selectedOption: any) => {
    if (selectedOption) {
      setFormDataDetails({
        ...formDataDetails,
        idTypeInput: selectedOption.value,
        typeInputLabel: selectedOption.label,
      });
    } else {
      setFormDataDetails({
        ...formDataDetails,
        idTypeInput: "",
        typeInputLabel: "",
      });
    }
  };

  // Permet de changer la valeur d'un input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormDataDetails = { ...formDataDetails, [name]: value };

    // Empêche une date de fin antérieure à la date de début
    if (name === "from" && updatedFormDataDetails.to && updatedFormDataDetails.to < value) {
      updatedFormDataDetails.to = value;
    }

    setFormDataDetails(updatedFormDataDetails);
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormDataDetails({ ...formDataDetails, [e.target.name]: e.target.value });
  };
  const handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormDataDetails({
      ...formDataDetails,
      [e.target.name]: e.target.checked,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Check si il y a un chevauchement de date
    const dateExist = handleCheckIfDateExist(
      formDataDetails.from,
      formDataDetails.to,
      datesExist
    );

    if (dateExist && formDataDetails.id !== Number(id)) {
      setIsOverlap(true);
      return;
    }

    // creation d'une valeur
    if (tabElements === "Valeurs" && createOrEdit === "create") {
      const dataToSend = {
        ...formDataDetails,
        amount: parseFloat(formDataDetails.amount.toString().replace(",", ".")),
      };

      createValeur(idEmployee, dataToSend)
        .then(() => {
          showToastSuccess(t("Valeur créée avec succès"));
          setUpdateSocial(!updateSocial);
          setFormDataDetails({});
          setProlongations(false);
          closeModal();
        })
        .catch((err) => {
          if (err.response.data.message === "Les bulletins sont validés") showToastError(t("Les bulletins ont déjà été validés."))
          else showToastError(t("Erreur lors de la création de la valeur"));
        });
    } else if (tabElements === "Absences" && createOrEdit === "create") {

      // creation d'une absence
      const dataToSend = {
        ...formDataDetails,
        previousFrom: prolongations ? formDataDetails.previousFrom : "",
        morning: formDataDetails.morning ? true : false,
        afternoon: formDataDetails.afternoon ? true : false,
      };

      createAbsence(idEmployee, dataToSend)
        .then(() => {
          showToastSuccess(t("Absence créée avec succès"));
          setUpdateSocial(!updateSocial);
          setFormDataDetails({});
          setProlongations(false);
          closeModal();
        })
        .catch((err) => {
          if (err.response.data.message === "Les bulletins sont validés") showToastError(t("Les bulletins ont déjà été validés."))
          else showToastError(t("Erreur lors de la création de la valeur"));
        });
    } else if (tabElements === "Absences" && createOrEdit === "edit") {
      // modification d'une absence
      const dataToSend = {
        ...formDataDetails,
        previousFrom: prolongations ? formDataDetails.previousFrom : "",
        morning: formDataDetails.morning ? true : false,
        afternoon: formDataDetails.afternoon ? true : false,
      };

      updateAbsence(Number(id), dataToSend)
        .then(() => {
          showToastSuccess(t("Absence créée avec succès"));
          setUpdateSocial(!updateSocial);
          setFormDataDetails({});
          setProlongations(false);
          closeModal();
        })
        .catch((err) => {
          if (err.response.data.message === "Les bulletins sont validés") {showToastError(t("Les bulletins ont déjà été validés."))}
          else showToastError(t("Erreur lors de la création de la valeur"));
        });
    } else if (tabElements === "Valeurs" && createOrEdit === "edit") {
      // modification d'une valeur
      const dataToSend = {
        ...formDataDetails,
        amount: parseFloat(formDataDetails.amount.toString().replace(",", ".")),
      };
      updateValeur(Number(id), dataToSend)
        .then(() => {
          showToastSuccess(t("Valeur modifiée avec succès"));
          setUpdateSocial(!updateSocial);
          setFormDataDetails({});
          closeModal();
        })
        .catch((err) => {
          if (err.response.data.message === "Les bulletins sont validés") {showToastError(t("Les bulletins ont déjà été validés."))}
          else showToastError(t("Erreur lors de la création de la valeur"));
        });
    }
  };

  const handleClose = () => {
    setProlongations(false);
    setFormDataDetails({});
    closeModal();
  };

  return (
    <ModalShell
      title={`${createOrEdit === "create" ? t("Créer") : t("Modifier")} ${
        tabElements === "Absences" ? t("une absence") : t("une valeur")
      }`}
      subtitle={
        createOrEdit === "create"
          ? t("Ajoutez un nouvel élément")
          : t("Modifiez les informations")
      }
      size="lg"
      onClose={handleClose}
      footer={
        <>
          <ModalButton variant="secondary" onClick={handleClose}>
            {t("Annuler")}
          </ModalButton>
          <ModalButton variant="primary" type="submit" form="elements-details-form">
            {createOrEdit === "create" ? t("Créer") : t("Modifier")}
          </ModalButton>
        </>
      }
    >
      {isOverlap && (
        <Overlap
          createOrEdit={createOrEdit}
          idEmployee={idEmployee}
          id={id}
          closeModal={closeModal}
        />
      )}

      <form
        id="elements-details-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 font-Nunito"
      >
        {/* Section Type et Valeur/Dates */}
        <ModalSection title={t("Informations principales")}>
          <div
            className={
              tabElements === "Valeurs"
                ? "grid grid-cols-1 gap-3 sm:grid-cols-2"
                : "flex flex-col gap-3"
            }
          >
            <SelectText
              fetchOptions={typesInputValeur}
              label={t("Type d'élément")}
              name="idTypeInput"
              value={formDataDetails.idTypeInput || ""}
              onChange={handleSelectTextChange}
              className={tabElements === "Valeurs" ? "" : "w-full"}
            />

            {tabElements === "Valeurs" ? (
              <Input
                label={t("Valeur")}
                name="amount"
                required={true}
                value={formDataDetails.amount || ""}
                onChange={handleChange}
                clasName=""
              />
            ) : (
              <div className="flex flex-col gap-3">
                {/* Dates de début et fin avec checkboxes */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Input
                      label={t("Date de début")}
                      name="from"
                      type="date"
                      required={true}
                      value={formDataDetails.from || ""}
                      onChange={handleChange}
                      clasName=""
                      max={dateRestrictions.max}
                      defaultValue={today}
                    />
                    <Checkbox
                      label={t("Après-midi")}
                      name="afternoon"
                      onChange={handleChangeCheckbox}
                      checked={formDataDetails.afternoon}
                      value={formDataDetails.afternoon}
                      clasName="text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Input
                      label={t("Date de fin")}
                      name="to"
                      type="date"
                      required={true}
                      value={formDataDetails.to || ""}
                      onChange={handleChange}
                      clasName=""
                      min={formDataDetails.from || dateRestrictions.min}
                      max={dateRestrictions.max}
                      defaultValue={today}
                    />
                    <Checkbox
                      label={t("Matin")}
                      name="morning"
                      onChange={handleChangeCheckbox}
                      checked={formDataDetails.morning}
                      value={formDataDetails.morning}
                      clasName="text-sm"
                    />
                  </div>
                </div>

                {/* Section Prolongations */}
                <div>
                  <Checkbox
                    label={t("Prolongations")}
                    name="prolongations"
                    onChange={() => setProlongations(!prolongations)}
                    checked={prolongations}
                    value="prolongations"
                    clasName="text-sm font-medium"
                  />
                  {prolongations && (
                    <div className="mt-3">
                      <Input
                        label={t("Date d'arret initial")}
                        name="previousFrom"
                        type="date"
                        required={true}
                        value={formDataDetails.previousFrom || ""}
                        onChange={handleChange}
                        clasName="w-full sm:w-2/3"
                        max={dateRestrictions.max}
                        defaultValue={today}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ModalSection>

        {/* Section Commentaire */}
        <ModalSection title={t("Commentaire")}>
          <TextArea
            label={t("Commentaire")}
            name="commentary"
            value={formDataDetails.commentary || ""}
            onChange={handleChangeTextArea}
            clasName="w-full"
          />
        </ModalSection>
      </form>
    </ModalShell>
  );
};

export default ElementsDetails;
